import { useMemo } from "react";

import { Link } from "react-router-dom";

import dayjs from "dayjs";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import type { PublishedMoveLine } from "~/types/contabilidad";

interface LibroMayorDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountCode: string;
  accountName: string;
  type: "debit" | "credit";
  lines: PublishedMoveLine[];
  isLoading: boolean;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function LibroMayorDetailDialog({
  open,
  onOpenChange,
  accountCode,
  accountName,
  type,
  lines,
  isLoading,
}: LibroMayorDetailDialogProps) {
  // Filter lines by type (debit > 0 or credit > 0)
  const filteredLines = useMemo(() => {
    return lines.filter((line) =>
      type === "debit" ? Number(line.debit) > 0 : Number(line.credit) > 0
    );
  }, [lines, type]);

  const total = useMemo(() => {
    return filteredLines.reduce(
      (acc, line) =>
        acc + (type === "debit" ? Number(line.debit) : Number(line.credit)),
      0
    );
  }, [filteredLines, type]);

  const title = type === "debit" ? "Débitos" : "Créditos";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-4xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            Detalle de {title} - {accountCode} {accountName}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {filteredLines.length} movimientos | Total: {formatCurrency(total)}
          </p>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-auto">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : filteredLines.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <p className="text-muted-foreground">
                No hay movimientos de {title.toLowerCase()} para esta cuenta.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Fecha</TableHead>
                    <TableHead className="w-[120px]">N° Asiento</TableHead>
                    <TableHead className="w-[100px]">Diario</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead className="w-[120px] text-right">
                      Monto
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLines.map((line, index) => (
                    <TableRow
                      key={line.line_id}
                      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                    >
                      <TableCell className="text-muted-foreground">
                        {dayjs(line.move_date).format("DD/MM/YYYY")}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        <Link
                          to={`/contabilidad/movimientos/${line.move_id}`}
                          className="text-blue-600 hover:underline"
                        >
                          {line.move_number}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {line.journal_name}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {line.description || line.move_ref || "-"}
                      </TableCell>
                      <TableCell
                        className={`text-right font-mono font-medium ${
                          type === "debit" ? "text-green-600" : "text-blue-600"
                        }`}
                      >
                        {formatCurrency(
                          type === "debit"
                            ? Number(line.debit)
                            : Number(line.credit)
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow className="bg-muted/50 font-semibold">
                    <TableCell colSpan={4}>TOTAL</TableCell>
                    <TableCell
                      className={`text-right font-mono ${
                        type === "debit" ? "text-green-600" : "text-blue-600"
                      }`}
                    >
                      {formatCurrency(total)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
